import React, { useMemo, useRef, useState } from "react";
import { RocketLaunchIcon, FolderIcon, CogIcon, GlobeAltIcon, CpuChipIcon } from "@heroicons/react/24/outline";

interface RelationshipRow {
  id: string;
  goal: string;
  project: string;
  projectStatus: string;
  practice: string;
  subPractice: string;
  technology: string;
}

interface RelationshipMapProps {
  customerData?: {
    top_10_projects?: {
      top_projects: Array<{
        project_id: string;
        name: string;
        modernization_plan_project?: string;
        goals?: Array<{ goal: string }>;
        presidio_areas?: string[];
      }>;
    };
  };
}

function convertCustomerDataToRelationshipRows(customerData?: RelationshipMapProps['customerData']): RelationshipRow[] {
  if (!customerData?.top_10_projects?.top_projects) {
    return [];
  }

  const rows: RelationshipRow[] = [];
  let idCounter = 1;

  customerData.top_10_projects.top_projects.forEach((project) => {
    const projectStatus = project.modernization_plan_project === 'Yes' ? 'Proposed' : 'Real';
    
    // For each goal in the project, create relationship rows
    if (project.goals && project.goals.length > 0) {
      project.goals.forEach((goal) => {
        // If we have presidio_areas, use them to derive practice/subPractice/technology
        if (project.presidio_areas && project.presidio_areas.length > 0) {
          project.presidio_areas.forEach((area) => {
            // Split area into practice and sub-practice/technology if possible
            const parts = area.split(' - ');
            const practice = parts[0] || 'General Practice';
            const subPractice = parts[1] || area;
            const technology = parts[2] || area;

            rows.push({
              id: `r${idCounter++}`,
              goal: goal.goal,
              project: project.name,
              projectStatus,
              practice,
              subPractice,
              technology,
            });
          });
        } else {
          // If no presidio_areas, create a default relationship
          rows.push({
            id: `r${idCounter++}`,
            goal: goal.goal,
            project: project.name,
            projectStatus,
            practice: 'General Practice',
            subPractice: 'Implementation',
            technology: 'TBD',
          });
        }
      });
    } else {
      // If no goals, still create a row for the project
      const area = project.presidio_areas?.[0] || 'General Practice';
      rows.push({
        id: `r${idCounter++}`,
        goal: 'General Goal',
        project: project.name,
        projectStatus,
        practice: area,
        subPractice: 'Implementation',
        technology: 'TBD',
      });
    }
  });

  return rows;
}

const columns = [
  { key: "goals", label: "Goals", icon: RocketLaunchIcon, accent: "from-sky-500/20 to-cyan-400/10", stroke: "#38bdf8", glow: "rgba(56,189,248,0.28)", edge: "#38bdf8" },
  { key: "projects", label: "Projects", icon: FolderIcon, accent: "from-violet-500/20 to-fuchsia-400/10", stroke: "#8b5cf6", glow: "rgba(139,92,246,0.28)", edge: "#8b5cf6" },
  { key: "practices", label: "Practices", icon: GlobeAltIcon, accent: "from-emerald-500/20 to-teal-400/10", stroke: "#10b981", glow: "rgba(16,185,129,0.28)", edge: "#10b981" },
  { key: "subPractices", label: "Sub-Practices", icon: CogIcon, accent: "from-amber-500/20 to-orange-400/10", stroke: "#f59e0b", glow: "rgba(245,158,11,0.28)", edge: "#f59e0b" },
  { key: "technologies", label: "Technologies", icon: CpuChipIcon, accent: "from-rose-500/20 to-pink-400/10", stroke: "#f43f5e", glow: "rgba(244,63,94,0.28)", edge: "#f43f5e" },
];

function slug(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function deriveGraphFromRows(rows: RelationshipRow[]) {
  const goalsMap = new Map<string, { id: string; label: string }>();
  const projectsMap = new Map<string, { id: string; label: string; status: string }>();
  const practicesMap = new Map<string, { id: string; label: string }>();
  const subPracticesMap = new Map<string, { id: string; label: string }>();
  const technologiesMap = new Map<string, { id: string; label: string }>();
  const edgeMap = new Map<string, { from: string; to: string; isProposed: boolean; hasReal: boolean; hasProposed: boolean }>();

  const addEdge = (from: string, to: string, isProposed: boolean) => {
    const key = `${from}->${to}`;
    const existing = edgeMap.get(key);
    if (!existing) {
      edgeMap.set(key, { from, to, isProposed, hasReal: !isProposed, hasProposed: isProposed });
      return;
    }
    const hasReal = existing.hasReal || !isProposed;
    const hasProposed = existing.hasProposed || isProposed;
    edgeMap.set(key, {
      ...existing,
      hasReal,
      hasProposed,
      isProposed: hasProposed && !hasReal,
    });
  };

  rows.forEach((row) => {
    const goalId = `goal-${slug(row.goal)}`;
    const projectId = `project-${slug(row.project)}`;
    const practiceId = `practice-${slug(row.practice)}`;
    const subPracticeId = `subpractice-${slug(row.subPractice)}`;
    const technologyId = `technology-${slug(row.technology)}`;

    goalsMap.set(goalId, { id: goalId, label: row.goal });
    projectsMap.set(projectId, { id: projectId, label: row.project, status: row.projectStatus });
    practicesMap.set(practiceId, { id: practiceId, label: row.practice });
    subPracticesMap.set(subPracticeId, { id: subPracticeId, label: row.subPractice });
    technologiesMap.set(technologyId, { id: technologyId, label: row.technology });

    const rowIsProposed = row.projectStatus === "Proposed";
    addEdge(goalId, projectId, rowIsProposed);
    addEdge(projectId, practiceId, rowIsProposed);
    addEdge(practiceId, subPracticeId, rowIsProposed);
    addEdge(subPracticeId, technologyId, rowIsProposed);
  });

  return {
    rows,
    goals: Array.from(goalsMap.values()),
    projects: Array.from(projectsMap.values()),
    practices: Array.from(practicesMap.values()),
    subPractices: Array.from(subPracticesMap.values()),
    technologies: Array.from(technologiesMap.values()),
    links: Array.from(edgeMap.values()),
  };
}

function makePath(a: { x: number; y: number; w: number; h: number }, b: { x: number; y: number; w: number; h: number }): string {
  const x1 = a.x + a.w;
  const y1 = a.y + a.h / 2;
  const x2 = b.x;
  const y2 = b.y + b.h / 2;
  const dx = x2 - x1;
  const arch = Math.max(18, Math.min(44, Math.abs(y2 - y1) * 0.12 + dx * 0.04));
  const c1 = x1 + dx * 0.28;
  const c2 = x1 + dx * 0.72;
  return `M ${x1} ${y1} C ${c1} ${y1 - arch}, ${c2} ${y2 + arch}, ${x2} ${y2}`;
}

function buildLayout(graph: any) {
  const width = 1440; // keep a 40px right gutter matching the left startX
  const headerY = 16;
  const headerH = 74;
  const colW = 240;
  const gap = 40;
  const nodeH = 64;
  const minInnerGap = 18;
  const maxInnerGap = 56;
  const startX = 40;
  const dividerY = headerY + headerH + 8;
  const topPadding = dividerY + 10;
  const bottomPadding = 60;

  const counts = columns.map((col) => graph[col.key].length);
  const tallestCount = Math.max(...counts, 1);
  const contentHeight = tallestCount * nodeH + Math.max(0, tallestCount - 1) * minInnerGap;
  const height = Math.max(880, topPadding + contentHeight + bottomPadding);
  const usableHeight = height - topPadding - bottomPadding;

  const adjacency = new Map();
  const reverseAdjacency = new Map();
  graph.links.forEach(({ from, to }: { from: string; to: string }) => {
    if (!adjacency.has(from)) adjacency.set(from, []);
    if (!reverseAdjacency.has(to)) reverseAdjacency.set(to, []);
    adjacency.get(from).push(to);
    reverseAdjacency.get(to).push(from);
  });

  const baseColumnLayout: Record<string, { gap: number; startY: number }> = {};
  columns.forEach((col) => {
    const count = graph[col.key].length;
    if (count === 0) {
      baseColumnLayout[col.key] = { gap: 0, startY: topPadding };
      return;
    }
    const totalNodeHeight = count * nodeH;
    const availableGapSpace = Math.max(0, usableHeight - totalNodeHeight);
    const computedGap = count > 1 ? Math.min(maxInnerGap, Math.max(minInnerGap, availableGapSpace / (count - 1))) : 0;
    const startY = topPadding;
    baseColumnLayout[col.key] = { gap: computedGap, startY };
  });

  const orderByColumn: Record<string, string[]> = {};
  columns.forEach((col) => {
    orderByColumn[col.key] = graph[col.key].map((item: { id: string }) => item.id);
  });

  const getColumnCenters = (columnKey: string) => {
    const ids = orderByColumn[columnKey] || [];
    const layoutInfo = baseColumnLayout[columnKey];
    const map = new Map();
    ids.forEach((id, index) => {
      map.set(id, layoutInfo.startY + index * (nodeH + layoutInfo.gap) + nodeH / 2);
    });
    return map;
  };

  const sortByNeighborBarycenter = (stableIds: string[], neighborLookup: Map<string, number>) => {
    const scored = stableIds.map((id, index) => {
      const neighbors = [...(adjacency.get(id) || []), ...(reverseAdjacency.get(id) || [])];
      const positions = neighbors.map((nId) => neighborLookup.get(nId)).filter((v): v is number => typeof v === "number");
      const score = positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : index * 1000;
      return { id, score, index };
    });
    scored.sort((a, b) => (a.score - b.score) || (a.index - b.index));
    return scored.map((item) => item.id);
  };

  orderByColumn.subPractices = sortByNeighborBarycenter(orderByColumn.subPractices, getColumnCenters("technologies"));
  orderByColumn.practices = sortByNeighborBarycenter(orderByColumn.practices, getColumnCenters("subPractices"));
  orderByColumn.projects = sortByNeighborBarycenter(orderByColumn.projects, getColumnCenters("practices"));
  orderByColumn.goals = sortByNeighborBarycenter(orderByColumn.goals, getColumnCenters("projects"));
  orderByColumn.technologies = sortByNeighborBarycenter(orderByColumn.technologies, getColumnCenters("subPractices"));

  const nodes: any[] = [];
  const nodeMap: Record<string, any> = {};

  columns.forEach((col, colIdx) => {
    const orderedIds = orderByColumn[col.key] || [];
    const itemsById = new Map(graph[col.key].map((item: { id: string }) => [item.id, item]));
    const layoutInfo = baseColumnLayout[col.key];

    orderedIds.forEach((id, itemIdx) => {
      const item = itemsById.get(id);
      if (!item) return;
      const x = startX + colIdx * (colW + gap);
      const y = layoutInfo.startY + itemIdx * (nodeH + layoutInfo.gap);
      const node = { ...item, type: col.key, columnIndex: colIdx, x, y, w: colW, h: nodeH };
      nodes.push(node);
      nodeMap[(item as { id: string }).id] = node;
    });
  });

  const edges = graph.links
    .map(({ from, to, isProposed }: { from: string; to: string; isProposed: boolean }, idx: number) => {
      const a = nodeMap[from];
      const b = nodeMap[to];
      if (!a || !b) return null;
      const sourceColumn = columns.find((c) => c.key === a.type);
      const targetColumn = columns.find((c) => c.key === b.type);
      return {
        id: `e-${idx}`,
        from,
        to,
        path: makePath(a, b),
        projected: isProposed,
        edgeColorA: sourceColumn?.edge || "#94a3b8",
        edgeColorB: targetColumn?.edge || "#94a3b8",
      };
    })
    .filter(Boolean);

  return { nodes, nodeMap, edges, width, height, headerH, headerY, dividerY };
}

function computeVisibility(graph: any, showProposed: boolean) {
  if (showProposed) {
    return {
      visibleNodeIds: new Set([
        ...graph.goals.map((n: { id: string }) => n.id),
        ...graph.projects.map((n: { id: string }) => n.id),
        ...graph.practices.map((n: { id: string }) => n.id),
        ...graph.subPractices.map((n: { id: string }) => n.id),
        ...graph.technologies.map((n: { id: string }) => n.id),
      ]),
      visibleEdgeIds: new Set(graph.links.map((_: any, idx: number) => `e-${idx}`)),
    };
  }

  const activeProjectIds = new Set<string>(graph.projects.filter((p: { status: string }) => p.status === "Real").map((p: { id: string }) => p.id));
  const adjacency = new Map();
  graph.links.forEach(({ from, to }: { from: string; to: string }) => {
    if (!adjacency.has(from)) adjacency.set(from, []);
    adjacency.get(from).push(to);
  });

  const visibleNodeIds = new Set<string>();
  const queue = Array.from(activeProjectIds);
  queue.forEach((id) => visibleNodeIds.add(id));

  while (queue.length) {
    const current = queue.shift();
    const next = adjacency.get(current) || [];
    next.forEach((id: string) => {
      if (!visibleNodeIds.has(id)) {
        visibleNodeIds.add(id);
        queue.push(id);
      }
    });
  }

  graph.links.forEach(({ from, to }: { from: string; to: string }) => {
    if (activeProjectIds.has(to)) visibleNodeIds.add(from);
  });

  const visibleEdgeIds = new Set();
  graph.links.forEach((edge: { from: string; to: string }, idx: number) => {
    if (visibleNodeIds.has(edge.from) && visibleNodeIds.has(edge.to)) {
      visibleEdgeIds.add(`e-${idx}`);
    }
  });

  return { visibleNodeIds, visibleEdgeIds };
}

function computeHighlightSet(selectedId: string | null, graph: any, visibleNodeIds: Set<string>): Set<string> {
  if (!selectedId || !visibleNodeIds.has(selectedId)) return new Set<string>();

  const forward = new Map();
  const backward = new Map();
  graph.links.forEach(({ from, to }: { from: string; to: string }) => {
    if (!visibleNodeIds.has(from) || !visibleNodeIds.has(to)) return;
    if (!forward.has(from)) forward.set(from, []);
    if (!backward.has(to)) backward.set(to, []);
    forward.get(from).push(to);
    backward.get(to).push(from);
  });

  const allNodes = [
    ...graph.goals.map((n: any) => ({ ...n, type: "goals" })),
    ...graph.projects.map((n: any) => ({ ...n, type: "projects" })),
    ...graph.practices.map((n: any) => ({ ...n, type: "practices" })),
    ...graph.subPractices.map((n: any) => ({ ...n, type: "subPractices" })),
    ...graph.technologies.map((n: any) => ({ ...n, type: "technologies" })),
  ];
  const selectedNode = allNodes.find((n) => n.id === selectedId);
  if (!selectedNode) return new Set<string>();

  const result = new Set<string>([selectedId]);
  const queue = [selectedId];

  if (selectedNode.type === "goals") {
    while (queue.length) {
      const current = queue.shift();
      const next = forward.get(current) || [];
      next.forEach((id: string) => {
        if (!result.has(id)) {
          result.add(id);
          queue.push(id);
        }
      });
    }
    return result;
  }

  while (queue.length) {
    const current = queue.shift();
    const next = [...(forward.get(current) || []), ...(backward.get(current) || [])];
    next.forEach((id: string) => {
      if (!result.has(id)) {
        result.add(id);
        queue.push(id);
      }
    });
  }
  return result;
}

interface NodeCardProps {
  node: any;
  activeIds: Set<string>;
  onToggle: (id: string) => void;
  isVisible: boolean;
}

function NodeCard({ node, activeIds, onToggle, isVisible }: NodeCardProps) {
  const hasSelection = activeIds.size > 0;
  const isActive = !hasSelection || activeIds.has(node.id);
  const isDimmed = hasSelection && !activeIds.has(node.id);
  const isProject = node.type === "projects";
  const column = columns.find((c) => c.key === node.type);

  if (!isVisible) return null;

  return (
    <g transform={`translate(${node.x},${node.y})`} onClick={() => onToggle(node.id)} className="cursor-pointer">
      <defs>
        <linearGradient id={`node-fill-${node.id}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.98)" />
          <stop offset="100%" stopColor="rgba(248,250,252,0.92)" />
        </linearGradient>
      </defs>
      <rect x="4" y="6" width={node.w} height={node.h} rx={20} fill={column?.glow || "rgba(148,163,184,0.18)"} opacity={isActive ? 0.9 : 0.12} />
      <rect width={node.w} height={node.h} rx={20} fill={`url(#node-fill-${node.id})`} stroke={column?.stroke || "#cbd5e1"} strokeWidth={isActive ? 2.4 : 1.1} opacity={isActive ? 1 : 0.35} filter="url(#shadow)" />
      <foreignObject x="14" y="10" width={node.w - 28} height={node.h - 20}>
        <div className="h-full w-full flex items-center justify-between gap-3 text-slate-800">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">
              {/* {node.type === "goals" ? "Outcome" : node.type === "projects" ? "Initiative" : node.type === "practices" ? "Domain" : node.type === "subPractices" ? "Capability" : "Technology"} */}
            </div>
            <div className={`text-[14px] font-semibold leading-tight ${isDimmed ? "text-slate-500" : "text-slate-900"}`}>{node.label}</div>
          </div>
          {isProject && (
            <div className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold border ${node.status === "Real" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
              {node.status}
            </div>
          )}
        </div>
      </foreignObject>
    </g>
  );
}

export default function RelationshipMap({ customerData }: RelationshipMapProps) {
  const relationshipRows = useMemo(() => convertCustomerDataToRelationshipRows(customerData), [customerData]);
  const [selected, setSelected] = useState<string | null>(null);
  const [showProposed, setShowProposed] = useState(false);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, originX: 0, originY: 0 });

  const graph = useMemo(() => deriveGraphFromRows(relationshipRows), [relationshipRows]);
  const layout = useMemo(() => buildLayout(graph), [graph]);
  const { visibleNodeIds, visibleEdgeIds } = useMemo(() => computeVisibility(graph, showProposed), [graph, showProposed]);
  const related = useMemo(() => computeHighlightSet(selected, graph, visibleNodeIds), [selected, graph, visibleNodeIds]);

  const visibleEdges = layout.edges
    .filter((e: any) => visibleEdgeIds.has(e.id))
    .filter((e: any) => !selected || (related.has(e.from) && related.has(e.to)));

  const clear = () => setSelected(null);
  const onToggle = (id: string) => setSelected((prev) => (prev === id ? null : id));

  const clampScale = (scale: number) => Math.max(0.55, Math.min(1.8, scale));
  const zoomBy = (factor: number) => {
    setView((prev) => ({ ...prev, scale: clampScale(prev.scale * factor) }));
  };
  const resetView = () => setView({ scale: 1, x: 0, y: 0 });
  const fitToView = () => {
    const padding = 36;
    const scaleX = (layout.width - padding * 2) / layout.width;
    const scaleY = (layout.height - padding * 2) / layout.height;
    const scale = clampScale(Math.min(scaleX, scaleY, 1));
    setView({ scale, x: padding, y: padding / 2 });
  };

  const handleWheel = (event: React.WheelEvent) => {
    if (!event.ctrlKey) return;
    event.preventDefault();
    const factor = event.deltaY < 0 ? 1.08 : 0.92;
    setView((prev) => ({ ...prev, scale: clampScale(prev.scale * factor) }));
  };

  const handlePointerDown = (event: React.PointerEvent) => {
    setIsPanning(true);
    panStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      originX: view.x,
      originY: view.y,
    };
  };

  const handlePointerMove = (event: React.PointerEvent) => {
    if (!isPanning) return;
    const dx = event.clientX - panStartRef.current.x;
    const dy = event.clientY - panStartRef.current.y;
    setView((prev) => ({
      ...prev,
      x: panStartRef.current.originX + dx,
      y: panStartRef.current.originY + dy,
    }));
  };

  const handlePointerUp = () => setIsPanning(false);

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.08),_transparent_30%),radial-gradient(circle_at_top_right,_rgba(168,85,247,0.08),_transparent_30%),linear-gradient(to_bottom,_#f8fafc,_#eef2ff)] p-0">
      <div className="w-full space-y-6">
        <div className="grid grid-cols-1 gap-6 items-start">
          <div className="rounded-3xl border-white/60 shadow-xl bg-white/80 backdrop-blur">
            <div className="p-6 space-y-4">
              <div className="flex flex-wrap gap-3">
                <button onClick={() => { setSelected(null); setShowProposed((prev) => !prev); }} className={`rounded-2xl px-4 py-2 ${showProposed ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>
                  {showProposed ? "Show Modernization Plan Projects" : "Show Modernization Plan Projects"}
                </button>
                <button onClick={clear} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                  Clear Highlights
                </button>
                <button onClick={() => zoomBy(1.12)} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                  Zoom In
                </button>
                <button onClick={() => zoomBy(0.9)} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                  Zoom Out
                </button>
                <button onClick={resetView} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                  Reset View
                </button>
                <button onClick={fitToView} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                  Fit to View
                </button>
              </div>

              <div
                className={`rounded-[28px] border border-slate-200/80 bg-white/90 overflow-hidden shadow-[0_20px_60px_rgba(15,23,42,0.08)] ${isPanning ? "cursor-grabbing" : "cursor-grab"}`}
                onWheel={handleWheel}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
              >
                <svg
                  viewBox={`0 0 ${layout.width} ${layout.height}`}
                  className="w-full h-auto min-w-[1400px] touch-none select-none"
                  onPointerDown={handlePointerDown}
                >
                  <defs>
                    <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="10" stdDeviation="10" floodOpacity="0.12" />
                    </filter>
                    <filter id="softGlow" x="-30%" y="-30%" width="160%" height="160%">
                      <feGaussianBlur stdDeviation="4" result="blur" />
                      <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                    <linearGradient id="bgGrid" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="rgba(255,255,255,1)" />
                      <stop offset="100%" stopColor="rgba(248,250,252,1)" />
                    </linearGradient>
                    {visibleEdges.map((edge: any) => (
                      <linearGradient key={`grad-${edge.id}`} id={`edge-grad-${edge.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor={edge.edgeColorA} stopOpacity="0.9" />
                        <stop offset="100%" stopColor={edge.edgeColorB} stopOpacity="0.9" />
                      </linearGradient>
                    ))}
                  </defs>

                  <rect x="0" y="0" width={layout.width} height={layout.height} fill="url(#bgGrid)" />

                  <g transform={`translate(${view.x} ${view.y}) scale(${view.scale})`}>
                    {columns.map((col, idx) => {
                      const Icon = col.icon;
                      const x = 40 + idx * (240 + 40);
                      return (
                        <g key={col.key}>
                          <rect x={x} y={layout.headerY} width={240} height={layout.headerH} rx={22} fill="rgba(255,255,255,0.72)" stroke={col.stroke} strokeOpacity="0.28" />
                          <foreignObject x={x + 16} y={layout.headerY + 10} width={208} height={54}>
                            <div className="flex items-center gap-3 h-full">
                              <div className={`h-11 w-11 rounded-2xl bg-gradient-to-br ${col.accent} flex items-center justify-center border border-white/70 shadow-sm`}>
                                <Icon className="h-5 w-5 text-slate-700" />
                              </div>
                              <div>
                               {/* <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Layer {idx + 1}</div> */}
                                <div className="text-[16px] font-semibold text-slate-800">{col.label}</div>
                              </div>
                            </div>
                          </foreignObject>
                        </g>
                      );
                    })}

                    <line
                      x1="24"
                      y1={layout.dividerY}
                      x2={layout.width - 24}
                      y2={layout.dividerY}
                      stroke="#000000"
                      strokeWidth="2"
                      strokeOpacity="0.9"
                    />

                    {visibleEdges.map((edge: any) => {
                      const isFocused = selected && related.has(edge.from) && related.has(edge.to);
                      return (
                        <g key={edge.id}>
                          <path
                            d={edge.path}
                            fill="none"
                            stroke={`url(#edge-grad-${edge.id})`}
                            strokeWidth={isFocused ? 9.2 : edge.projected ? 6.4 : 5.1}
                            strokeDasharray={edge.projected ? "7 10" : undefined}
                            strokeOpacity={isFocused ? 0.34 : 0.2}
                            filter="url(#softGlow)"
                          />
                          <path
                            d={edge.path}
                            fill="none"
                            stroke={`url(#edge-grad-${edge.id})`}
                            strokeWidth={isFocused ? 4.8 : edge.projected ? 3.1 : 2.7}
                            strokeDasharray={edge.projected ? "7 10" : undefined}
                            strokeLinecap="round"
                            strokeOpacity={isFocused ? 1 : selected ? 0.28 : 0.84}
                          />
                        </g>
                      );
                    })}

                    {layout.nodes.map((node: any) => (
                      <NodeCard key={node.id} node={node} activeIds={related} onToggle={onToggle} isVisible={visibleNodeIds.has(node.id)} />
                    ))}
                  </g>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
